import { useEffect, useState, useCallback } from 'react';
import api from '../../api';

function BookingManager() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ status: 'pending', confirmed_date: '', confirmed_time: '', message: '' });
  const [filter, setFilter] = useState('all');
  const [auditLogs, setAuditLogs] = useState({});

  const fetchAuditForBooking = useCallback(async (bookingId) => {
    try {
      const res = await api.get(`/api/admin/bookings/${bookingId}/audit?_=${Date.now()}`);
      setAuditLogs(prev => ({ ...prev, [bookingId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setAuditLogs(prev => ({ ...prev, [bookingId]: [] }));
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/bookings');
      const list = res.data || [];
      setBookings(list);
      list.forEach(b => fetchAuditForBooking(b.id));
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAuditForBooking]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const openEdit = (booking) => {
    setEditingId(booking.id);
    setEditForm({
      status: booking.status || 'pending',
      confirmed_date: booking.confirmed_date || '',
      confirmed_time: booking.confirmed_time || '',
      message: booking.message ?? ''
    });
  };

  const closeEdit = () => setEditingId(null);

  const handleSave = async (bookingId) => {
    try {
      await api.put(`/api/admin/bookings/${bookingId}`, editForm);
      await fetchBookings();
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao guardar');
    }
  };

  const handleQuickConfirm = async (booking) => {
    const date = prompt('Data confirmada (AAAA-MM-DD):', booking.requested_date || '');
    if (date === null) return;
    const time = prompt('Hora confirmada (HH:MM):', booking.requested_time || '');
    if (time === null) return;
    try {
      await api.put(`/api/admin/bookings/${booking.id}`, {
        status: 'confirmed',
        confirmed_date: date,
        confirmed_time: time,
        message: booking.message ?? ''
      });
      await fetchBookings();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao confirmar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apagar esta marcação?')) return;
    try {
      await api.delete(`/api/admin/bookings/${id}`);
      fetchBookings();
      if (editingId === id) closeEdit();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao apagar');
    }
  };

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#2ecc71';
      case 'cancelled': return '#e74c3c';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--background)',
    color: 'var(--text)',
    border: '1px solid var(--border-strong, rgba(255,255,255,0.12))',
    borderRadius: '6px',
    fontFamily: 'inherit',
    fontSize: '0.88rem',
    transition: 'border-color 0.3s ease'
  };

  if (loading) return <div className="loading">A carregar marcações...</div>;

  return (
    <div>
      <div className="admin-header">
        <h1>Marcações</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {['all', 'pending', 'confirmed', 'cancelled'].map(f => (
            <button key={f} type="button" className={`btn btn-small ${filter === f ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(f)}>
              {{ all: 'Todas', pending: 'Pendentes', confirmed: 'Confirmadas', cancelled: 'Canceladas' }[f]}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' }}>
        {filtered.map(booking => {
          const isEditing = editingId === booking.id;
          const log = auditLogs[booking.id] || [];
          const messageLog = log.filter(e => e.field_name === 'message').sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          const otherLog = log.filter(e => e.field_name !== 'message').sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const statusColor = getStatusColor(booking.status);

          return (
            <div key={booking.id} style={{
              background: 'var(--background-secondary)',
              border: '1px solid var(--border, rgba(255,255,255,0.06))',
              borderLeft: `3px solid ${statusColor}`,
              borderRadius: 'var(--radius-lg, 14px)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', fontFamily: 'var(--font-heading)' }}>{booking.name}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{booking.email}</div>
                  {booking.phone && <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{booking.phone}</div>}
                </div>
                <span style={{
                  color: statusColor,
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  flexShrink: 0,
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: `1px solid ${statusColor}40`,
                  background: `${statusColor}12`
                }}>
                  {getStatusLabel(booking.status)}
                </span>
              </div>

              <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', padding: '10px 14px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pedido:</span> {booking.requested_date || '—'} {booking.requested_time || ''}
                {(booking.confirmed_date || booking.confirmed_time) && (
                  <span style={{ display: 'block', marginTop: '4px', color: 'var(--text)' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Confirmado:</span> {booking.confirmed_date || '—'} {booking.confirmed_time || ''}
                  </span>
                )}
              </div>

              {booking.message && (
                <div style={{
                  fontSize: '0.88rem',
                  color: 'var(--text)',
                  padding: '14px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '100px',
                  overflowY: 'auto',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Mensagem atual</div>
                  {booking.message}
                </div>
              )}

              <div style={{ border: '1px solid var(--border, rgba(255,255,255,0.06))', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', background: 'rgba(201,160,80,0.1)', fontWeight: 600, fontSize: '0.75rem', color: 'var(--accent)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Log de mensagens ({messageLog.length})
                </div>
                <div style={{ padding: '10px 14px', fontSize: '0.82rem', maxHeight: '180px', overflowY: 'auto', background: 'rgba(0,0,0,0.1)' }}>
                  {messageLog.length === 0 ? (
                    <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Sem alterações às notas.</span>
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {messageLog.map((entry, idx) => (
                        <li key={entry.id || idx} style={{ padding: '6px 0', borderBottom: idx < messageLog.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '3px' }}>
                            {new Date(entry.created_at).toLocaleString('pt-PT')} — <strong>{entry.admin_username}</strong>
                          </div>
                          <div style={{ color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.4' }}>
                            {entry.new_value || '—'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {otherLog.length > 0 && (
                <div style={{ border: '1px solid var(--border, rgba(255,255,255,0.06))', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '8px 14px', background: 'rgba(201,160,80,0.06)', fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Alterações registadas ({otherLog.length})
                  </div>
                  <div style={{ padding: '10px 14px', fontSize: '0.78rem', maxHeight: '120px', overflowY: 'auto', background: 'rgba(0,0,0,0.1)' }}>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', color: 'var(--text-secondary)' }}>
                      {otherLog.map((entry, i) => {
                        const label = { status: 'Estado', confirmed_date: 'Data', confirmed_time: 'Hora' }[entry.field_name] || entry.field_name;
                        return (
                          <li key={entry.id || i} style={{ padding: '3px 0' }}>
                            <strong>{entry.admin_username}</strong> alterou {label}: «{entry.old_value || '—'}» → «{entry.new_value || '—'}» ({new Date(entry.created_at).toLocaleString('pt-PT')})
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              {isEditing ? (
                <div style={{
                  border: '1px solid var(--accent)',
                  borderRadius: '8px',
                  padding: '18px',
                  background: 'var(--accent-subtle, rgba(201,160,80,0.05))',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notas</label>
                    <textarea
                      value={editForm.message}
                      onChange={e => setEditForm({ ...editForm, message: e.target.value })}
                      rows={3}
                      placeholder="Editar notas"
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Estado</label>
                      <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} style={inputStyle}>
                        <option value="pending">Pendente</option>
                        <option value="confirmed">Confirmada</option>
                        <option value="cancelled">Cancelada</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Data</label>
                      <input type="date" value={editForm.confirmed_date} onChange={e => setEditForm({ ...editForm, confirmed_date: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Hora</label>
                      <input type="time" value={editForm.confirmed_time} onChange={e => setEditForm({ ...editForm, confirmed_time: e.target.value })} style={inputStyle} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={closeEdit} className="btn btn-small btn-secondary">Cancelar</button>
                    <button type="button" onClick={() => handleSave(booking.id)} className="btn btn-small btn-primary">Guardar</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', flexWrap: 'wrap' }}>
                  {booking.status !== 'confirmed' && (
                    <button onClick={() => handleQuickConfirm(booking)} className="btn btn-small" style={{ backgroundColor: '#2ecc71', color: '#fff', border: 'none' }}>Confirmar</button>
                  )}
                  <button onClick={() => openEdit(booking)} className="btn btn-small btn-secondary">Editar</button>
                  <button onClick={() => fetchAuditForBooking(booking.id)} className="btn btn-small btn-secondary">Atualizar log</button>
                  <button onClick={() => handleDelete(booking.id)} className="btn btn-small btn-danger">Apagar</button>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '60px' }}>
            Nenhuma marcação com este filtro.
          </p>
        )}
      </div>
    </div>
  );
}

export default BookingManager;
